
import {isFunction,isObject} from 'underscore';
import {getLogger} from 'loglevel';
import {getMergedError,fillErrorInstance} from './errorHandling';
import {STORAGE_HANDLER_KEY} from './requestStorageHandlers';
import {EXPIRY_HANDLER_KEY} from './requestExpiryHandlers';

const log = getLogger('RequestPipeline');

/**
 * See conceptual documentation here:
 * https://arrowecommerce.atlassian.net/wiki/display/ARW/Request+Pipelines
 */
export class RequestPipeline {

  /**
   * @param requestOptions
   * @param requestOptions.url
   * @param requestOptions.storageOptions.enabled [boolean]
   * @param requestOptions.storageOptions.expiryHandlerKey [EXPIRY_HANDLER_KEY]
   * @param requestOptions.storageOptions.storageHandlerKey [STORAGE_HANDLER_KEY]
   * @param requestOptions.retryOptions.enabled [boolean]
   * @param requestOptions.retryOptions.attempts [number]
   */
  constructor(requestOptions = {}) {
    this.pipeline = [];
    this.meta = {};
    this.isRunning = false;
    this.statusCode = null;
    this.response = null;
    this.error = null;
    this.failedAttempts = [];
    this.successfulAttempts = [];
    this.prepareRequestOptions(requestOptions);
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    this.requestId = this.getUniqueId();
  }

  /**
   * @private
   * @param requestOptions
   */
  prepareRequestOptions(requestOptions = {}) {
    this.requestOptions = {
      method: 'GET',
      ...requestOptions
    };
    this.storageOptions = {
      enabled: false,
      storageHandlerKey: STORAGE_HANDLER_KEY.SESSION,
      expiryHandlerKey: EXPIRY_HANDLER_KEY.ONE_DAY,
      ...requestOptions.storageOptions
    };
    this.retryOptions = {
      enabled: false,
      attempts: 0,
      ...requestOptions.retryOptions
    };
  }

  /**
   * @private
   * @param middlewareFunction
   */
  wrapMiddleware(middlewareFunction) {
    return () => {
      return middlewareFunction(this);
    }
  }

  /**
   * @private
   */
  shouldRetry() {
    let shouldRetry = false;
    if (this.failedAttempts.length > 10) {
      return false;
    } else if (this.retryOptions.enabled) {
      return this.failedAttempts.length < this.retryOptions.attempts;
    } else {
      return false;
    }
    return shouldRetry;
  }

  /**
   * Run the pipeline
   * @private
   * @return {Promise.<RequestPipeline>}
   */
  runPipeline() {
    if (!this.pipeline.length) return;
    if (this.isRunning) return;
    this.isRunning = true;
    function chainPromises(lastPromise, nextHandler) {
      return lastPromise.then(nextHandler);
    }
    return this.pipeline
      .reduce(chainPromises, Promise.resolve(this))
      .then(() => {
        this.error = null;
        this.isRunning = false;
        this.successfulAttempts.push(new Date());
        this.resolve(this);
        return this;
      }, (err) => {
        this.pipeLineFailed(err);
      });
  }

  /**
   * @private
   * @param {Error} err A native JavaScript error thrown from middleware
   */
  pipeLineFailed(err) {
    this.isRunning = false;
    let requestErrorProperties = this.error || { type: 'REQUEST_PIPELINE_FAILED' };
    let requestError = getMergedError(requestErrorProperties);
    fillErrorInstance(err, requestError);
    this.failedAttempts.push(requestError);
    if (this.shouldRetry()) {
      log.trace(`Request pipeline failed, retrying request for ${this.requestOptions.url}`, this);
      this.runPipeline();
    } else {
      this.error = requestError;
      err.request = this;
      this.reject(err);
    }
  }

  /**
   * Load meta and response data from a cached
   * state into the request
   *
   * @param stateData
   * @param {Object} stateData.meta Any meta data
   * @param {Object} stateData.response Any response data
   */
  loadState(stateData) {
    if (!isObject(stateData)) return;
    if (stateData.meta) {
      Object.assign(this.meta, stateData.meta);
    }
    if (stateData.response) {
      this.response = stateData.response;
    }
  }

  /**
   * Return a string which uniquely describes this request
   * this may be as simple as the method and the url but
   * sometimes we need to customise this
   *
   * @param requestOptions
   * @param request
   * @return {string}
   */
  getUniqueId(requestOptions = this.requestOptions, request = this) {
    if (isFunction(requestOptions.uniqueIdHandler)) {
      return requestOptions.uniqueIdHandler(request);
    } else {
      return requestOptions.method + ':' + requestOptions.url;
    }
  }

  /**
   * Add a middleware function to the pipeline
   *
   * @param middlewareFunction
   */
  use(middlewareFunction) {
    if (isFunction(middlewareFunction)) {
      this.pipeline.push(this.wrapMiddleware(middlewareFunction));
    }
    return this;
  }

  /**
   * Return the size of the middleware pipeline
   *
   * @return {Number}
   */
  pipelineSize() {
    return this.pipeline.length;
  }

  /**
   * Run the pipeline and make the requests
   *
   * @return {Promise}
   */
  run() {
    if (!this.isRunning) this.runPipeline();
    return this.promise;
  }

  /**
   * Has this pipeline been run yet
   * @return {boolean}
   */
  isActivated() {
    return this.isRunning || this.successfulAttempts.length > 0;
  }

  /**
   * Customise how this gets serialized with JSON.stringify
   */
  toJSON() {
    return {
      meta: this.meta,
      response: this.response
    };
  }
}



// WEBPACK FOOTER //
// ./assets/utilities/RequestPipeline.js